// User and Authentication
export interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
  role: UserRole;
  permissions: string[];
}

export enum UserRole {
  ADMIN = 'admin',
  INVENTORY_MANAGER = 'inventory_manager',
  INVENTORY_STAFF = 'inventory_staff',
  VIEWER = 'viewer'
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
}

// Purchase Orders
export interface PurchaseOrder {
  po_id: string;
  vendor_code: string;
  vendor_name?: string;
  po_date: string;
  total_qty: number;
  matched_qty: number;
  status: POStatus;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export enum POStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  ABNORMAL = 'abnormal'
}

export interface ExpectedItem {
  item_id: string;
  po_id: string;
  sequence: string;
  product_name: string;
  serial_number?: string;
  imei?: string;
  match_status: MatchStatus;
  matched_at?: string;
  batch_id?: string;
}

export enum MatchStatus {
  PENDING = 'pending',
  MATCHED = 'matched',
  UNMATCHED = 'unmatched',
  ABNORMAL = 'abnormal'
}

// Inventory
export interface InventoryItem {
  po_number?: string;
  batch_id: string;
  product_name: string;
  serial_number?: string;
  imei?: string;
  check_timestamp: string;
  vendor_code: string;
  source_code: string;
  sequence_number: number;
}

export interface ScanRequest {
  serial_number?: string;
  imei?: string;
  vendor_code: string;
}

export interface MatchResult {
  success: boolean;
  batch_id?: string;
  product_name?: string;
  message: string;
  match_type?: 'serial' | 'imei' | 'product' | 'manual';
}

// Unmatched Items
export interface UnmatchedItem {
  temp_id: string;
  serial_number?: string;
  imei?: string;
  product_name: string;
  vendor_code: string;
  received_date: string;
  waiting_days: number;
  status: UnmatchedStatus;
  notes?: string;
}

export enum UnmatchedStatus {
  WAITING = 'waiting',
  MATCHED = 'matched',
  ABNORMAL = 'abnormal',
  RETURNED = 'returned'
}

// Vendors
export interface Vendor {
  vendor_id: string;
  vendor_code: string;
  vendor_name: string;
  contact_person?: string;
  phone?: string;
  email?: string;
  address?: string;
  payment_terms?: string;
  created_date: string;
  status: VendorStatus;
  performance_score?: number;
}

export enum VendorStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended'
}

// Dashboard and Reports
export interface DashboardData {
  today_stats: {
    total_scanned: number;
    matched: number;
    unmatched: number;
    match_rate: number;
  };
  pending_items: number;
  unmatched_items: UnmatchedItem[];
  vendor_performance: VendorPerformance[];
  recent_activities: Activity[];
}

export interface VendorPerformance {
  vendor_code: string;
  vendor_name: string;
  total_orders: number;
  fulfillment_rate: number;
  average_match_rate: number;
  pending_items: number;
}

export interface Activity {
  id: string;
  type: ActivityType;
  description: string;
  user: string;
  timestamp: string;
  details?: any;
}

export enum ActivityType {
  IMPORT = 'import',
  SCAN = 'scan',
  MATCH = 'match',
  UNMATCH = 'unmatch',
  RESOLVE = 'resolve',
  EXPORT = 'export'
}

// API Responses
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pages: number;
  limit: number;
}

export interface PaginationParams {
  page: number;
  limit: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

// Form Types
export interface ImportFormData {
  file: File;
  vendor_code: string;
  po_date: string;
  notes?: string;
}

export interface ScanFormData {
  vendor_code: string;
  serial_number?: string;
  imei?: string;
}

// Navigation
export interface NavItem {
  name: string;
  href: string;
  icon?: React.ComponentType<any>;
  current?: boolean;
  children?: NavItem[];
}

// UI State
export interface ToastMessage {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
}

export interface Modal {
  isOpen: boolean;
  title: string;
  content?: React.ReactNode;
  onClose: () => void;
  onConfirm?: () => void;
}

// Store States
export interface AppState {
  // Auth
  auth: AuthState;
  
  // UI
  loading: boolean;
  toasts: ToastMessage[];
  
  // Data
  vendors: Vendor[];
  purchaseOrders: PurchaseOrder[];
  selectedVendor: string | null;
  selectedPO: string | null;
}

// Utility Types
export type Status = 'idle' | 'loading' | 'success' | 'error';

export interface LoadingState {
  status: Status;
  error?: string;
}